import { HxSkeleton } from "./hx/HxSkeleton";
import { Skeleton } from "./ui/skeleton";

/**
 * BootSkeleton — what the user sees before the app can show anything real:
 * while the session is being checked at boot, and for the instant between a
 * route match and its lazily-loaded page arriving.
 *
 * It copies the frame's geometry (Shell.tsx and AdminShell.tsx, the template's
 * sidebar since passes 019 and 020): a 17rem sidebar, 3rem when collapsed and
 * none below 768px, with the header, rows and footer the real one has; a 48px
 * top bar; then the page-title row and work area, so the page that replaces it
 * lands in place with nothing jumping. It is drawn from plain elements, not
 * the real SidebarProvider, so a placeholder takes no keystrokes or focus.
 *
 * The theme is whatever the pre-paint script in index.html already put on
 * <html>, so a dark-mode user never sees a white flash and the stored choice
 * is read in one place. Record: docs/testing/ui-migration/021-boot-skeleton.md.
 *
 * Replaces three grey bars on a blank page, which read as "broken" rather
 * than "loading".
 */
const COLLAPSE_KEY = "helix-shell-collapsed"; // must match Shell.tsx

function readPref(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

export default function BootSkeleton({ label = "Loading" }: { label?: string }) {
  const theme = document.documentElement.classList.contains("dark") ? "dark" : "light";
  const collapsed = readPref(COLLAPSE_KEY) === "1";
  // /admin's top bar holds only the theme button; /ops's adds the bell and
  // the workspace control.
  const onAdmin = window.location.pathname.startsWith("/admin");
  const navItems = 9;
  return (
    <div
      className={"helix" + (collapsed ? " collapsed" : "")}
      data-theme={theme}
      role="status"
      aria-busy="true"
      aria-label={label}
      style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}
    >
      <div className="flex min-h-0 flex-1">
        {/* Sidebar: header row, one row per nav entry, the person. */}
        <div
          aria-hidden="true"
          className={
            "hidden shrink-0 flex-col border-r bg-sidebar md:flex " + (collapsed ? "w-12" : "w-[17rem]")
          }
        >
          <div className="flex flex-col gap-2 p-2">
            <div className="flex h-8 items-center gap-2 px-2">
              <Skeleton className="size-4 shrink-0" />
              {!collapsed && <Skeleton className="h-4 w-28" />}
            </div>
          </div>
          <div className="flex min-h-0 flex-1 flex-col gap-0 p-2">
            {Array.from({ length: navItems }).map((_, i) => (
              <div key={i} className="flex h-8 items-center gap-2 px-2">
                <Skeleton className="size-4 shrink-0" />
                {!collapsed && <Skeleton className="h-3" style={{ width: i === 0 ? "48%" : i % 3 === 0 ? "70%" : "58%" }} />}
              </div>
            ))}
          </div>
          <div className="flex flex-col gap-2 p-2">
            <div className="flex h-12 items-center gap-2 px-2">
              <Skeleton className="size-8 shrink-0 rounded-lg" />
              {!collapsed && (
                <div className="flex flex-1 flex-col gap-1.5">
                  <Skeleton className="h-3 w-24" />
                  <Skeleton className="h-2.5 w-16" />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="main flex min-w-0 flex-1 flex-col">
          {/* Top bar: collapse and search on the left; theme, bell and
              workspace on the right (theme only on /admin). */}
          <div aria-hidden="true" className="flex h-12 shrink-0 items-center justify-between gap-2 border-b px-4 lg:px-6">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <Skeleton className="size-7 shrink-0" />
              {!onAdmin && <Skeleton className="h-8 w-full max-w-sm" />}
            </div>
            <div className="flex items-center gap-2">
              <Skeleton className="size-8" />
              {!onAdmin && (
                <>
                  <Skeleton className="size-8" />
                  <Skeleton className="h-8 w-28" />
                </>
              )}
            </div>
          </div>
          <div className="section-bar" aria-hidden="true">
            <div className="flex flex-col gap-2">
              <Skeleton className="h-5 w-56" />
              <Skeleton className="h-3 w-80" />
            </div>
          </div>
          <div className="work">
            <div className="work-inner">
              <HxSkeleton variant="page" label={label} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
